import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren, ViewEncapsulation, computed, effect, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { AutoFocusModule } from 'primeng/autofocus';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { ChatService } from './agent.service';
import { ThreadTypeFilter, isRuleRunThread, mergeThreadsByType } from './thread.service';
import { fullDateTime, shortDateTime } from '../../shared/utils/format-datetime';
import { ExportarConversacionComponent } from '../documentos/exportar-conversacion.component';

/** Opciones del filtro de tipo (app#690) — compartidas con la página de historial. */
export const THREAD_TYPE_FILTER_OPTIONS: ReadonlyArray<{
  value: ThreadTypeFilter; labelKey: string; icon: string;
}> = [
  { value: 'all', labelKey: 'agent.filterAll', icon: 'pi-list' },
  { value: 'chats', labelKey: 'agent.filterChats', icon: 'pi-comment' },
  { value: 'rules', labelKey: 'agent.filterRules', icon: 'pi-shield' },
];

const FILTER_STORAGE_KEY = 'synapseguard_agent_thread_filter';

/** Preferencia de filtro persistida (compartida entre selector y página). */
export function loadStoredThreadFilter(): ThreadTypeFilter {
  try {
    const stored = localStorage.getItem(FILTER_STORAGE_KEY);
    if (stored === 'chats' || stored === 'rules' || stored === 'all') return stored;
  } catch { /* storage no disponible */ }
  return 'all';
}

export function storeThreadFilter(filter: ThreadTypeFilter): void {
  try {
    localStorage.setItem(FILTER_STORAGE_KEY, filter);
  } catch { /* storage no disponible */ }
}

/**
 * Listado de conversaciones del agente, compartido entre sus dos casas:
 *
 * - `variant="sidebar"`: bajo el botón «Agente» del sidebar de la app
 *   (escritorio). Solo se renderiza con la pestaña del agente activa.
 * - `variant="dialog"`: dentro del p-dialog que abre el botón flotante del
 *   composer (móvil/tablet, donde no hay sidebar lateral).
 *
 * Toda la lógica de filas (búsqueda, renombrado inline, borrado con
 * confirmación inline, borrado masivo, prefetch por hover) vivía en
 * AgentComponent y se movió aquí tal cual. Abrir una conversación navega a
 * `/agent?thread_id=…` — el mismo deep-link de siempre — para que
 * AgentComponent pinte skeleton + scroll con un único camino de carga.
 */
@Component({
  selector: 'sg-agent-thread-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    AutoFocusModule,
    ExportarConversacionComponent,
    TranslatePipe
  ],
  templateUrl: './thread-list.component.html',
  styleUrls: ['./thread-list.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AgentThreadListComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() variant: 'dialog' | 'sidebar' = 'dialog';

  /** El usuario abrió una conversación o creó una nueva (el diálogo se cierra). */
  @Output() navigated = new EventEmitter<void>();

  /** Input de renombrado inline. Vive dentro de *ngFor + ngIf (solo existe uno
   *  a la vez), así que la única forma de enfocarlo al aparecer es observar los
   *  cambios de la QueryList. */
  @ViewChildren('threadRenameInput', { read: ElementRef })
  private threadRenameInputs!: QueryList<ElementRef<HTMLInputElement>>;
  private renameInputSubscription?: Subscription;

  /** Timer de intención de hover para el prefetch del historial (una fila a la vez). */
  private hoverPrefetchTimer?: ReturnType<typeof setTimeout>;

  readonly threadSearchQuery = signal('');
  isLoadingThreads = false;

  // ===== Filtro de tipo estilo Claude (app#690) =====
  // Un ÚNICO listado: «Todo» mezcla chats y ejecuciones de regla por actividad
  // reciente (icono por tipo); «Chats»/«Alertas» aíslan cada uno. Sustituye a la
  // sección plegada de app#688, que metía un segundo scrollbar en el rail.
  readonly typeFilter = signal<ThreadTypeFilter>(loadStoredThreadFilter());
  readonly filterMenuOpen = signal(false);
  readonly filterOptions = THREAD_TYPE_FILTER_OPTIONS;

  /** Threads del filtro activo + búsqueda. `computed` memoizado (app#564). */
  readonly filteredThreads = computed(() => {
    const threads = mergeThreadsByType(
      this.chatService.threads(), this.chatService.ruleThreads(), this.typeFilter(),
    );
    const query = this.threadSearchQuery().trim().toLowerCase();
    if (!query) {
      return threads;
    }
    return threads.filter(thread =>
      thread.title?.toLowerCase().includes(query) ||
      thread.thread_id.toLowerCase().includes(query)
    );
  });

  // Renombrado inline en la propia fila y confirmación de borrado inline. Sin
  // modales propios superpuestos al p-dialog (quedarían bajo su máscara).
  editingThreadId: string | null = null;
  newThreadTitle: string = '';
  isSavingThreadTitle: boolean = false;
  confirmingDeleteThreadId: string | null = null;
  deletingThreadId: string | null = null;

  /** Confirmación armada para "eliminar todas" (dos clics, como en cada fila). */
  confirmingDeleteAll = false;
  isDeletingAll = false;
  /** Resultado del último borrado masivo, para poder contar lo que no cayó. */
  deleteAllFailed = 0;


  private lastStreaming = false;

  constructor(
    private readonly chatService: ChatService,
    private readonly router: Router,
    private readonly translate: TranslateService
  ) {
    // En el sidebar el listado está siempre a la vista (no se recarga al abrir
    // un diálogo), así que se refresca solo en los bordes del turno local:
    // al ARRANCAR aparece el thread recién creado y al TERMINAR llega su
    // título autogenerado.
    effect(() => {
      const streaming = this.chatService.isStreaming();
      if (streaming === this.lastStreaming) return;
      this.lastStreaming = streaming;
      setTimeout(() => void this.refreshThreads(), streaming ? 400 : 1500);
    });
  }

  ngOnInit(): void {
    void this.refreshThreads();
  }

  ngAfterViewInit(): void {
    // Enfocar + seleccionar el input de renombrado inline en cuanto aparece en
    // el DOM (está dentro de *ngFor + ngIf, no hay referencia estática posible).
    this.renameInputSubscription = this.threadRenameInputs.changes.subscribe(
      (inputs: QueryList<ElementRef<HTMLInputElement>>) => {
        const input = inputs.first?.nativeElement;
        if (input && this.editingThreadId) {
          input.focus();
          input.select();
        }
      }
    );
  }

  ngOnDestroy(): void {
    this.renameInputSubscription?.unsubscribe();
    this.clearHoverPrefetchTimer();
  }

  /** Recarga los listados desde la API (sin caché). Los dos tipos a la vez:
   *  «Todo» es el filtro por defecto y necesita ambos para pintar completo. */
  async refreshThreads(): Promise<void> {
    try {
      this.isLoadingThreads = true;
      await Promise.all([
        this.chatService.listAllThreads(),
        this.chatService.listRuleThreads(),
      ]);
    } catch (error) {
      console.error('❌ Error cargando threads desde API:', error);
    } finally {
      this.isLoadingThreads = false;
    }
  }

  /** Buscador solo en el diálogo (móvil): en el rail sobra — pedido de Raúl. */
  showSearch(): boolean {
    return this.variant === 'dialog';
  }

  // ===== Filtro de tipo (app#690) =====

  toggleFilterMenu(): void {
    this.filterMenuOpen.set(!this.filterMenuOpen());
  }

  setTypeFilter(filter: ThreadTypeFilter): void {
    this.typeFilter.set(filter);
    storeThreadFilter(filter);
    this.filterMenuOpen.set(false);
  }

  isRuleThread(thread: any): boolean {
    return isRuleRunThread(thread);
  }

  /** «Ver todas»: la página de historial (búsqueda + filtro + selección). */
  goToHistory(): void {
    void this.router.navigate(['/agent/historial']);
    this.navigated.emit();
  }

  isActiveThread(thread: any): boolean {
    return this.chatService.getCurrentThreadId() === thread.thread_id;
  }

  /** Puede haber VARIOS en streaming a la vez: preguntar en un chat nuevo no
   *  detiene los que ya trabajaban. En filas de regla decide el `status` del
   *  servidor (esas conversaciones las conduce el batch, no este cliente). */
  isStreamingThread(thread: any): boolean {
    return this.chatService.isThreadRunning(thread.thread_id)
      || (isRuleRunThread(thread) && thread.status === 'busy');
  }

  /** Conversación parada en un HITL: espera aprobar/editar/rechazar. */
  /**
   * ¿Esta conversación espera una decisión del analista (icono de pausa)?
   *
   * Solo en las conversaciones de ALERTA. LangGraph deja el thread en
   * `interrupted` en DOS casos que no se distinguen por status: cuando hay un
   * HITL de verdad esperando aprobación y cuando alguien DETIENE la ejecución a
   * mano. En un chat normal, «parado» no es «esperando»: marcarlo con el icono
   * de pausa era ruido. En un hilo de regla sí lo es — ahí el plan espera al
   * analista y por eso se sigue marcando.
   */
  isAwaitingDecisionThread(thread: any): boolean {
    if (!isRuleRunThread(thread)) return false;
    return this.chatService.isThreadAwaitingDecision(thread.thread_id)
      || thread.status === 'interrupted';
  }

  /**
   * Abre una conversación navegando al deep-link `/agent?thread_id=…`:
   * AgentComponent (suscrito a queryParams) carga el historial con skeleton,
   * caché de sesión y scroll al final — el mismo camino que los enlaces de
   * Actividad reciente.
   */
  /** Conversación que nombra la dirección actual. Es lo que decide si una
   *  navegación va a tener efecto, y no siempre coincide con la que el servicio
   *  tiene por actual: mientras una apertura está en vuelo, la URL ya ha cambiado
   *  y el servicio todavía no. */
  private get threadEnLaDireccion(): string | null {
    return (this.router.parseUrl(this.router.url).queryParams['thread_id'] as string) ?? null;
  }

  private openThread(threadId: string): void {
    // Pulsar la conversación que ya está abierta: el Router descarta una
    // navegación a la misma URL sin decir nada, así que esto no hacía NADA salvo
    // cerrar el desplegable — justo el gesto con el que uno intenta recuperar una
    // vista que se ha quedado rara. Ahora refresca de verdad.
    //
    // Se compara con la DIRECCIÓN, no con la conversación que el servicio tiene
    // por actual: si se está abriendo otra y aún no ha contestado, el servicio
    // sigue nombrando la anterior, y confundir las dos cosas convertía «volver a
    // la de antes» en un refresco que no navegaba — dejando viva justo la carga
    // que había que cancelar.
    if (threadId === this.threadEnLaDireccion) {
      this.chatService.refreshCurrentThread();
      this.navigated.emit();
      return;
    }
    void this.router.navigate(['/agent'], { queryParams: { thread_id: threadId } });
    this.navigated.emit();
  }

  /**
   * "Nueva conversación" NO crea un thread en el servidor (app#570) — solo
   * limpia la UI y el deep-link. El thread se crea de forma perezosa al enviar
   * el primer mensaje.
   */
  createNewThread(): void {
    this.chatService.startNewConversation();
    void this.router.navigate(['/agent']);
    this.navigated.emit();
  }

  /**
   * Clic en una fila del listado: abrir la conversación — salvo que la fila
   * esté en un estado transitorio (edición, confirmación de borrado o borrado
   * en curso). Un clic fuera de una confirmación pendiente la descarta.
   */
  onThreadRowClick(thread: any): void {
    if (this.editingThreadId === thread.thread_id || this.deletingThreadId === thread.thread_id) {
      return;
    }
    if (this.confirmingDeleteThreadId) {
      this.confirmingDeleteThreadId = null;
      return;
    }
    this.openThread(thread.thread_id);
  }

  /**
   * Enter con el foco puesto en la propia fila (navegación por teclado).
   * Se ignoran los Enter que burbujeen desde botones/inputs internos.
   */
  onThreadRowKeydown(event: Event, thread: any): void {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    this.onThreadRowClick(thread);
  }

  /**
   * Prefetch con intención: tras ~120ms de hover sostenido sobre una fila se
   * precarga su historial, así el clic posterior abre la conversación al
   * instante desde la caché de sesión.
   */
  onThreadRowHover(thread: any): void {
    this.clearHoverPrefetchTimer();
    this.hoverPrefetchTimer = setTimeout(
      () => this.chatService.prefetchThreadHistory(thread.thread_id),
      120
    );
  }

  onThreadRowHoverEnd(): void {
    this.clearHoverPrefetchTimer();
  }

  /**
   * mousedown = intención inequívoca de abrir: prefetch inmediato, gana el
   * tiempo entre mousedown y click. Se ignora si el gesto va dirigido a las
   * acciones de la fila (editar/borrar/confirmar).
   */
  onThreadRowPress(thread: any, event: Event): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.thread-actions, .thread-delete-confirm, .thread-inline-actions')) return;
    this.chatService.prefetchThreadHistory(thread.thread_id);
  }

  private clearHoverPrefetchTimer(): void {
    if (this.hoverPrefetchTimer) {
      clearTimeout(this.hoverPrefetchTimer);
      this.hoverPrefetchTimer = undefined;
    }
  }

  // ----- Borrado con confirmación inline (sin confirm() nativo) -----

  requestDeleteThread(thread: any, event: Event): void {
    event.stopPropagation();
    this.cancelEditingThreadTitle();
    this.confirmingDeleteThreadId = thread.thread_id;
  }

  cancelDeleteThread(event?: Event): void {
    event?.stopPropagation();
    this.confirmingDeleteThreadId = null;
  }

  /**
   * Segundo clic (botón "Eliminar" de la confirmación): borra de verdad.
   * El service quita la fila de forma optimista, así que no hay recarga aquí.
   */
  async confirmDeleteThread(threadId: string, event: Event): Promise<void> {
    event.stopPropagation();
    if (this.deletingThreadId) return;

    this.deletingThreadId = threadId;
    const eraLaConversacionAbierta = threadId === this.chatService.getCurrentThreadId();
    try {
      await this.chatService.deleteThread(threadId);
      // La vista se vacía, pero la URL seguía nombrando la conversación borrada.
      // Al escribir de nuevo nacía otro thread SIN que la URL lo siguiera, así que
      // un F5 resucitaba el enlace muerto y la conversación recién empezada solo
      // se podía recuperar buscándola a mano en el listado.
      if (eraLaConversacionAbierta) {
        void this.router.navigate(['/agent'], { queryParams: {} });
      }
    } catch (error) {
      console.error('❌ Error eliminando thread:', error);
    } finally {
      this.deletingThreadId = null;
      this.confirmingDeleteThreadId = null;
    }
  }

  // ===== Borrado masivo del listado =====

  requestDeleteAllThreads(): void {
    this.cancelEditingThreadTitle();
    this.cancelDeleteThread();
    this.deleteAllFailed = 0;
    this.confirmingDeleteAll = true;
  }

  cancelDeleteAllThreads(): void {
    this.confirmingDeleteAll = false;
  }

  /**
   * Elimina las conversaciones QUE ESTÁN LISTADAS — con un filtro de búsqueda
   * activo, solo esas. El contador del botón dice siempre cuántas son, para que
   * no haya sorpresas.
   */
  async confirmDeleteAllThreads(): Promise<void> {
    if (this.isDeletingAll) return;
    const ids = this.filteredThreads().map(t => t.thread_id);
    if (!ids.length) {
      this.confirmingDeleteAll = false;
      return;
    }

    this.isDeletingAll = true;
    try {
      const { failed } = await this.chatService.deleteThreads(ids);
      this.deleteAllFailed = failed;
    } catch (error) {
      console.error('❌ Error eliminando conversaciones:', error);
    } finally {
      this.isDeletingAll = false;
      this.confirmingDeleteAll = false;
    }
  }

  /** Nº de conversaciones a borrar que están esperando una decisión: su plan
   *  pendiente deja de ser reanudable, y eso hay que avisarlo. */
  awaitingDecisionInSelection(): number {
    return this.filteredThreads().filter(t => this.isAwaitingDecisionThread(t)).length;
  }

  // ----- Títulos y fechas -----

  getThreadDisplayTitle(thread: any): string {
    const title = thread.title || thread.metadata?.title || '';
    if (title && !this.chatService.isDefaultThreadTitle(title)) return title;
    // Sin nombre propio: si nació de un run automático de regla, decirlo.
    if (thread.metadata?.origin === 'alerts-agent-batch') {
      return this.translate.instant('agent.ruleRunThread');
    }
    return title || `Thread ${thread.thread_id.substring(0, 8)}`;
  }

  /**
   * Fecha de la conversación: «Hoy 08:49», «Ayer 03:15», «17 ago 08:49». El
   * relativo («Hace 3 h») decía cuánto hacía pero no a qué hora, y a partir de
   * un día se volvía impreciso; esto responde a las dos cosas y ocupa igual.
   */
  getThreadDate(thread: any): string {
    const date = thread.updated_at || thread.created_at;
    if (!date) return '';
    return shortDateTime(date, this.translate.currentLang, this.dayLabels());
  }

  /** Fecha completa (con segundos) para el `title` de la fila. */
  getThreadDateTitle(thread: any): string {
    const date = thread.updated_at || thread.created_at;
    return date ? fullDateTime(date, this.translate.currentLang) : '';
  }

  private dayLabels(): { today: string; yesterday: string } {
    return {
      today: this.translate.instant('common.time.today'),
      yesterday: this.translate.instant('common.time.yesterday'),
    };
  }

  // ----- Renombrado inline (el lápiz convierte la fila en un input) -----

  startEditingThreadTitle(thread: any, event: Event): void {
    event.stopPropagation();
    this.confirmingDeleteThreadId = null;
    this.editingThreadId = thread.thread_id;
    this.newThreadTitle = this.getThreadDisplayTitle(thread);
  }

  cancelEditingThreadTitle(): void {
    this.editingThreadId = null;
    this.newThreadTitle = '';
  }

  /**
   * Guarda el título editado. El service actualiza el signal de la lista de
   * forma optimista, así que no hay recarga completa después de guardar.
   */
  async saveThreadTitle(thread: any): Promise<void> {
    if (this.editingThreadId !== thread.thread_id || this.isSavingThreadTitle) return;

    const title = this.newThreadTitle.trim();
    if (!title || title === this.getThreadDisplayTitle(thread)) {
      this.cancelEditingThreadTitle();
      return;
    }

    this.isSavingThreadTitle = true;
    try {
      await this.chatService.updateThreadTitle(thread.thread_id, title);
      this.cancelEditingThreadTitle();
    } catch (error) {
      // Mantener el modo edición para que el usuario no pierda lo escrito
      console.error('❌ Error actualizando título:', error);
    } finally {
      this.isSavingThreadTitle = false;
    }
  }

  /**
   * Blur del input de renombrado: guarda si hay cambios; si no, cierra la
   * edición. Los botones ✓/✕ hacen preventDefault en mousedown para que su
   * clic no dispare este blur antes de tiempo.
   */
  onRenameBlur(thread: any): void {
    if (this.editingThreadId !== thread.thread_id || this.isSavingThreadTitle) return;
    const title = this.newThreadTitle.trim();
    if (title && title !== this.getThreadDisplayTitle(thread)) {
      void this.saveThreadTitle(thread);
    } else {
      this.cancelEditingThreadTitle();
    }
  }

  trackByThread(_: number, thread: any): string {
    return thread.thread_id;
  }
}
